import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScriptSettings } from "@/hooks/useScripts";

interface EditorSettingsBarProps {
  settings: ScriptSettings;
  setSettings: (settings: ScriptSettings) => void;
  canUseMirrorMode: () => boolean;
}

export function EditorSettingsBar({
  settings,
  setSettings,
  canUseMirrorMode,
}: EditorSettingsBarProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-card border-2 border-black rounded-xl p-4 mb-4 shadow-[4px_4px_0_#1A1A1A]">
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-4">
        {/* Font Size */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold">{t("editor.settings.fontSize")}</Label>
            <span className="text-xs font-bold bg-primary/20 px-1.5 py-0.5 rounded border border-black border-dashed">
              {settings.fontSize}px
            </span>
          </div>
          <Slider
            value={[settings.fontSize]}
            min={24}
            max={120}
            step={4}
            onValueChange={([value]) => setSettings({ ...settings, fontSize: value })}
            className="py-1"
          />
        </div>

        {/* Scroll Speed */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold">{t("editor.settings.scrollSpeed", "滾動速度")}</Label>
            <span className="text-xs font-bold bg-primary/20 px-1.5 py-0.5 rounded border border-black border-dashed">
              {settings.scrollSpeed || 50}
            </span>
          </div>
          <Slider
            value={[settings.scrollSpeed || 50]}
            min={10}
            max={100}
            step={5}
            onValueChange={([value]) => setSettings({ ...settings, scrollSpeed: value })}
            className="py-1"
          />
        </div>

        {/* Line Height */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold">{t("editor.settings.lineHeight", "行高")}</Label>
            <span className="text-xs font-bold bg-primary/20 px-1.5 py-0.5 rounded border border-black border-dashed">
              {settings.lineHeight ?? 1.5}
            </span>
          </div>
          <Slider
            value={[settings.lineHeight ?? 1.5]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={([value]) => setSettings({ ...settings, lineHeight: value })}
            className="py-1"
          />
        </div>

        {/* Horizontal Padding */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold">{t("editor.settings.horizontalPadding", "左右邊距")}</Label>
            <span className="text-xs font-bold bg-primary/20 px-1.5 py-0.5 rounded border border-black border-dashed">
              {settings.horizontalPadding ?? 10}%
            </span>
          </div>
          <Slider
            value={[settings.horizontalPadding ?? 10]}
            min={5}
            max={40}
            step={5}
            onValueChange={([value]) => setSettings({ ...settings, horizontalPadding: value })}
            className="py-1"
          />
        </div>

        {/* Colors - Full Width on Mobile */}
        <div className="col-span-2 lg:col-span-1 grid grid-cols-2 gap-2 p-2 border-2 border-black border-dashed rounded-lg bg-white/50">
          <div className="flex items-center justify-center gap-2">
            <Label className="text-xs font-bold whitespace-nowrap">{t("editor.settings.fontColor")}</Label>
            <div className="relative">
              <input
                type="color"
                value={settings.fontColor}
                onChange={(e) => setSettings({ ...settings, fontColor: e.target.value })}
                className="w-6 h-6 rounded border-2 border-black cursor-pointer appearance-none p-0 overflow-hidden"
                style={{ backgroundColor: settings.fontColor }}
              />
            </div>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Label className="text-xs font-bold whitespace-nowrap">{t("editor.settings.bgColor")}</Label>
            <div className="relative">
              <input
                type="color"
                value={settings.bgColor}
                onChange={(e) => setSettings({ ...settings, bgColor: e.target.value })}
                className="w-6 h-6 rounded border-2 border-black cursor-pointer appearance-none p-0 overflow-hidden"
                style={{ backgroundColor: settings.bgColor }}
              />
            </div>
          </div>
        </div>

        {/* Mirror Mode - Full Width on Mobile */}
        <div className="col-span-2 lg:col-span-1 flex items-center justify-between gap-2 p-2 border-2 border-black border-dashed rounded-lg bg-white/50">
          <Label className="flex items-center gap-2 text-xs font-bold whitespace-nowrap">
            <span style={{ paddingLeft: '16px' }}>
              {t("editor.settings.mirrorMode")}
            </span>
            {!canUseMirrorMode() && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 border-2 border-black text-black bg-yellow-400">
                PRO
              </Badge>
            )}
          </Label>
          <Switch
            checked={settings.mirrorMode}
            onCheckedChange={(checked) => setSettings({ ...settings, mirrorMode: checked })}
            disabled={!canUseMirrorMode()}
            className="scale-90 data-[state=checked]:bg-[#FF6B9D] border-2 border-black"
          />
        </div>
      </div>
    </div>
  );
}
